import React, { useEffect, useState, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Collapse,
  IconButton,
  Grid,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

// Orden de los 8 paneles (= keys del setting aiStatsVisibility).
const VISIBILITY_KEYS = [
  "horasAhorradas",
  "cobertura",
  "reclamosATiempo",
  "iaVsHumano",
  "recomendaciones",
  "embudoKpis",
  "objetoReclamo",
  "tags",
];

const ASSISTANT_TYPES = ["satisfaccion", "venta", "recupero"];

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3), maxWidth: 820 },
  paper: { padding: theme.spacing(3), marginTop: theme.spacing(2) },
  field: { marginBottom: theme.spacing(2) },
  sectionTitle: {
    fontWeight: 700,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  help: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    marginBottom: theme.spacing(1),
  },
  assistantCard: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  techHeader: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    marginTop: theme.spacing(1),
  },
  actions: { display: "flex", gap: theme.spacing(2), marginTop: theme.spacing(2), flexWrap: "wrap" },
  jsonField: { fontFamily: "monospace", fontSize: 12 },
}));

const SuperAdminAIStats = () => {
  const classes = useStyles();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [visibility, setVisibility] = useState({});
  const [assistants, setAssistants] = useState([]);
  const [techOpen, setTechOpen] = useState({}); // assistantId → bool
  const [loading, setLoading] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingAssistant, setSavingAssistant] = useState(0); // assistantId en curso

  const t = (k, opts) => i18n.t(`superAIStats.${k}`, opts);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/superadmin/dashboard");
        setCompanies(data.companies || []);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  const fetchConfig = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/superadmin/companies/${id}/ai-stats`);
      setEnabled(!!data.enabled);
      setVisibility(data.visibility || {});
      // technicalConfig de cada asistente → forma editable (queueRoles/jsonFieldMap como texto JSON).
      setAssistants(
        (data.assistants || []).map((a) => ({
          ...a,
          _queueRolesText: JSON.stringify(
            a.technicalConfig?.queueRoles || {},
            null,
            2
          ),
          _jsonFieldMapText: JSON.stringify(
            a.technicalConfig?.jsonFieldMap || {},
            null,
            2
          ),
        }))
      );
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, []);

  const handleSelectCompany = (id) => {
    setCompanyId(id);
    fetchConfig(id);
  };

  const handleSaveCompany = async () => {
    if (!companyId) return;
    setSavingCompany(true);
    try {
      const { data } = await api.put(`/superadmin/companies/${companyId}/ai-stats`, {
        enabled,
        visibility,
      });
      setEnabled(!!data.enabled);
      setVisibility(data.visibility || {});
      toast.success(t("saveOk"));
    } catch (err) {
      toastError(err);
    }
    setSavingCompany(false);
  };

  // muta un campo del asistente local
  const setAssistantField = (id, key, value) =>
    setAssistants((list) =>
      list.map((a) => (a.aiAssistantId === id ? { ...a, [key]: value } : a))
    );

  const setTechField = (id, key, value) =>
    setAssistants((list) =>
      list.map((a) =>
        a.aiAssistantId === id
          ? { ...a, technicalConfig: { ...a.technicalConfig, [key]: value } }
          : a
      )
    );

  const handleSaveAssistant = async (a) => {
    if (!companyId) return;
    // Validar JSON de queueRoles / jsonFieldMap antes de mandar.
    let queueRoles;
    let jsonFieldMap;
    try {
      queueRoles = JSON.parse(a._queueRolesText || "{}");
      jsonFieldMap = JSON.parse(a._jsonFieldMapText || "{}");
    } catch (e) {
      toast.error(t("invalidJson"));
      return;
    }
    setSavingAssistant(a.aiAssistantId);
    try {
      const tc = a.technicalConfig || {};
      const payload = {
        displayName: a.displayName,
        type: a.type,
        technicalConfig: {
          humanClosingHour: Number(tc.humanClosingHour),
          humanWorkMarginHours: Number(tc.humanWorkMarginHours),
          timeoutHours: Number(tc.timeoutHours),
          sinRespuestaWarnPct: Number(tc.sinRespuestaWarnPct),
          queueRoles,
          jsonFieldMap,
        },
      };
      const { data } = await api.put(
        `/superadmin/companies/${companyId}/ai-stats/assistant/${a.aiAssistantId}`,
        payload
      );
      // refrescar desde la respuesta autoritativa
      setEnabled(!!data.enabled);
      setVisibility(data.visibility || {});
      setAssistants(
        (data.assistants || []).map((x) => ({
          ...x,
          _queueRolesText: JSON.stringify(
            x.technicalConfig?.queueRoles || {},
            null,
            2
          ),
          _jsonFieldMapText: JSON.stringify(
            x.technicalConfig?.jsonFieldMap || {},
            null,
            2
          ),
        }))
      );
      toast.success(t("saveAssistantOk"));
    } catch (err) {
      toastError(err);
    }
    setSavingAssistant(0);
  };

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>
        {t("title")}
      </Typography>

      <FormControl fullWidth size="small" variant="outlined" className={classes.field}>
        <InputLabel>{t("company")}</InputLabel>
        <Select
          value={companyId}
          onChange={(e) => handleSelectCompany(e.target.value)}
          label={t("company")}
        >
          <MenuItem value="">
            <em>{t("selectCompany")}</em>
          </MenuItem>
          {companies.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name} (ID {c.id})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading && <CircularProgress size={24} />}

      {!loading && companyId && (
        <>
          {/* Habilitación + visibilidad */}
          <Paper className={classes.paper} elevation={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={t("enabled")}
            />
            <div className={classes.help}>{t("enabledHelp")}</div>

            <Divider style={{ margin: "16px 0" }} />

            <Typography className={classes.sectionTitle}>
              {t("visibilityTitle")}
            </Typography>
            <div className={classes.help}>{t("visibilityHelp")}</div>
            <Grid container>
              {VISIBILITY_KEYS.map((k) => (
                <Grid item xs={12} sm={6} key={k}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={visibility[k] !== false}
                        disabled={!enabled}
                        onChange={(e) =>
                          setVisibility((v) => ({ ...v, [k]: e.target.checked }))
                        }
                        color="primary"
                      />
                    }
                    label={t(`panels.${k}`)}
                  />
                </Grid>
              ))}
            </Grid>

            <div className={classes.actions}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                disabled={savingCompany}
                onClick={handleSaveCompany}
              >
                {savingCompany ? t("saving") : t("saveCompany")}
              </Button>
            </div>
          </Paper>

          {/* Asistentes */}
          <Typography className={classes.sectionTitle} style={{ marginTop: 24 }}>
            {t("assistantsTitle")}
          </Typography>
          {assistants.length === 0 ? (
            <Paper className={classes.paper} elevation={1}>
              {t("noAssistants")}
            </Paper>
          ) : (
            assistants.map((a) => (
              <Paper key={a.aiAssistantId} className={classes.assistantCard} elevation={1}>
                <Typography variant="subtitle2" color="textSecondary">
                  {t("catalogName")}: {a.name}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  label={t("displayName")}
                  value={a.displayName || ""}
                  onChange={(e) =>
                    setAssistantField(a.aiAssistantId, "displayName", e.target.value)
                  }
                  className={classes.field}
                  style={{ marginTop: 8 }}
                />
                <FormControl size="small" fullWidth className={classes.field}>
                  <InputLabel>{t("type")}</InputLabel>
                  <Select
                    value={a.type || "satisfaccion"}
                    onChange={(e) =>
                      setAssistantField(a.aiAssistantId, "type", e.target.value)
                    }
                  >
                    {ASSISTANT_TYPES.map((ty) => (
                      <MenuItem key={ty} value={ty}>
                        {t(`types.${ty}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <div
                  className={classes.techHeader}
                  onClick={() =>
                    setTechOpen((o) => ({
                      ...o,
                      [a.aiAssistantId]: !o[a.aiAssistantId],
                    }))
                  }
                >
                  <IconButton size="small">
                    {techOpen[a.aiAssistantId] ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                  <Typography variant="body2">{t("technicalConfig")}</Typography>
                </div>
                <Collapse in={!!techOpen[a.aiAssistantId]}>
                  <Grid container spacing={2} style={{ marginTop: 4 }}>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={t("tech.humanClosingHour")}
                        value={a.technicalConfig?.humanClosingHour ?? ""}
                        onChange={(e) =>
                          setTechField(
                            a.aiAssistantId,
                            "humanClosingHour",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={t("tech.humanWorkMarginHours")}
                        value={a.technicalConfig?.humanWorkMarginHours ?? ""}
                        onChange={(e) =>
                          setTechField(
                            a.aiAssistantId,
                            "humanWorkMarginHours",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label={t("tech.timeoutHours")}
                        value={a.technicalConfig?.timeoutHours ?? ""}
                        onChange={(e) =>
                          setTechField(
                            a.aiAssistantId,
                            "timeoutHours",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        inputProps={{ step: "0.01", min: 0, max: 1 }}
                        label={t("tech.sinRespuestaWarnPct")}
                        value={a.technicalConfig?.sinRespuestaWarnPct ?? ""}
                        onChange={(e) =>
                          setTechField(
                            a.aiAssistantId,
                            "sinRespuestaWarnPct",
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        size="small"
                        label={t("tech.queueRoles")}
                        value={a._queueRolesText}
                        onChange={(e) =>
                          setAssistantField(
                            a.aiAssistantId,
                            "_queueRolesText",
                            e.target.value
                          )
                        }
                        InputProps={{ classes: { input: classes.jsonField } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        size="small"
                        label={t("tech.jsonFieldMap")}
                        value={a._jsonFieldMapText}
                        onChange={(e) =>
                          setAssistantField(
                            a.aiAssistantId,
                            "_jsonFieldMapText",
                            e.target.value
                          )
                        }
                        InputProps={{ classes: { input: classes.jsonField } }}
                      />
                    </Grid>
                  </Grid>
                </Collapse>

                <div className={classes.actions}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<SaveIcon />}
                    disabled={savingAssistant === a.aiAssistantId}
                    onClick={() => handleSaveAssistant(a)}
                  >
                    {savingAssistant === a.aiAssistantId
                      ? t("saving")
                      : t("saveAssistant")}
                  </Button>
                </div>
              </Paper>
            ))
          )}
        </>
      )}
    </div>
  );
};

export default SuperAdminAIStats;
